import { useMemo, useState } from 'react';

import {
  Button,
  FieldSet,
  Heading,
  Label,
  LabeledControl,
  Legend,
  Modal,
  Radio,
  TextButton,
  Toast,
  useToast,
} from '@zillow/constellation-10';

import { logError } from '@zg-rentals/log-error';
import { ReleaseListingReasonType } from '@zg-rentals/ts-rental-manager-api-client';
import {
  useRentalManagerUserClaimableListingsWebApiHandler_claimCustomerListings,
  useRentalManagerUserClaimableListingsWebApiHandler_releaseCustomerListings,
} from '@zg-rentals/ts-rental-manager-api-client-react-query';

import propertyUtils from '../../../utils/propertyUtils';

import {
  emitIClaimedByMistakeEvent,
  emitINoLongerManageEvent,
  emitMyCompanyNoLongerManagesEvent,
  I_CLAIMED_BY_MISTAKE_EVENT_ID,
  I_NO_LONGER_MANAGE_EVENT_ID,
  MY_COMPANY_NO_LONGER_MANAGES_EVENT_ID,
} from '../../../analytics/events/claimDashboard';

import { useAuth } from '../../AuthProvider';

import type { ZrmClickstreamUser } from '@zg-rentals/clickstream';
import type { AddressComponents, DatarouterError } from '@zg-rentals/rental-manager-env';
import type {
  ClaimCustomerListingsResponseDto,
  ReleaseCustomerListingsResponseDto,
} from '@zg-rentals/ts-rental-manager-api-client';

const RADIO_CONTROL_NAME = 'RELEASE_CLAIM_REASONS';
const RELEASE_CLAIM_REASONS = [
  {
    label: 'I no longer manage this listing, or don’t want to be the manager of it',
    name: ReleaseListingReasonType.USER_NOT_LONGER_MANAGE,
    getEventFn: (user: ZrmClickstreamUser) => emitINoLongerManageEvent({ user }),
  },
  {
    label: 'My company no longer manages this listing',
    name: ReleaseListingReasonType.COMPANY_NOT_LONGER_MANAGE,
    getEventFn: (user: ZrmClickstreamUser) => emitMyCompanyNoLongerManagesEvent({ user }),
  },
  {
    label: 'I claimed this listing by mistake',
    name: ReleaseListingReasonType.CLAIMED_BY_MISTAKE,
    getEventFn: (user: ZrmClickstreamUser) => emitIClaimedByMistakeEvent({ user }),
  },
];

const ReleaseClaimModal = ({
  onClose,
  listing,
  listingRoute,
}: {
  onClose: () => void;
  listing: {
    address: AddressComponents;
    name: string;
    listingAlias: string;
    adproduct: { unpaidCustomerId: string };
  };
  listingRoute: string;
}) => {
  const { enqueueToast } = useToast();
  const { user } = useAuth();

  const [isLoading, setIsLoading] = useState(false);
  const [reason, setReason] = useState(RELEASE_CLAIM_REASONS[0].name);

  const { mutateAsync: releaseClaim } = useRentalManagerUserClaimableListingsWebApiHandler_releaseCustomerListings();
  const { mutateAsync: claimListing } = useRentalManagerUserClaimableListingsWebApiHandler_claimCustomerListings();

  const {
    address,
    name,
    listingAlias,
    adproduct: { unpaidCustomerId: customerId },
  } = listing;

  const listingDisplayName = name ? name : address.street;
  const { formattedCompleteAddress } = propertyUtils.formatAddress(address);

  const releaseClaimDataEventId = useMemo(() => {
    if (reason === ReleaseListingReasonType.USER_NOT_LONGER_MANAGE) {
      return I_NO_LONGER_MANAGE_EVENT_ID;
    }
    if (reason === ReleaseListingReasonType.COMPANY_NOT_LONGER_MANAGE) {
      return MY_COMPANY_NO_LONGER_MANAGES_EVENT_ID;
    }
    return I_CLAIMED_BY_MISTAKE_EVENT_ID;
  }, [reason]);

  const handleReleaseClaimClick = () => {
    if (!user) return;

    setIsLoading(true);
    releaseClaim({
      postData: {
        customerId,
        listingAliasesRequest: [
          {
            listingAlias,
            reasonType: reason,
          },
        ],
      },
    })
      .then((response: ReleaseCustomerListingsResponseDto) => {
        if (response.errors.length === 0) {
          const selectedReason = RELEASE_CLAIM_REASONS.find((r) => r.name === reason);
          const eventFn = selectedReason?.getEventFn;

          if (eventFn) {
            eventFn?.(user as ZrmClickstreamUser);
          }

          enqueueToast(
            <Toast
              tone="success"
              actionButton={
                <TextButton onClick={handleUndoClick} onImpact>
                  Undo claim release
                </TextButton>
              }
            >
              {`Your claim for ${listingDisplayName} was released successfully`}
            </Toast>,
            {
              duration: 0,
            },
          );
        } else {
          handleError(response.errors[0].errorMessage);
        }
      })
      .catch((error: DatarouterError) => {
        handleError(error.message ?? '');
      })
      .finally(() => {
        setIsLoading(false);
        onClose();
      });
  };

  const handleUndoClick = () => {
    claimListing({
      postData: {
        customerId,
        listingAliases: [listingAlias],
      },
    })
      .then((response: ClaimCustomerListingsResponseDto) => {
        if (response.errors.length === 0) {
          enqueueToast(<Toast tone="success">{`You reclaimed ${listingDisplayName} successfully`}</Toast>);
        } else {
          handleError(response.errors[0].errorMessage, false);
        }
      })
      .catch((error: DatarouterError) => {
        handleError(error.message ?? '', false);
      });
  };

  const handleError = (message: string, isRelease = true) => {
    enqueueToast(
      <Toast
        tone="critical"
        actionButton={
          <TextButton asChild onImpact>
            <a href={'mailto:rentalpartners@zillowgroup.com'}>Get help</a>
          </TextButton>
        }
      >
        {`${listingDisplayName} could not be ${isRelease ? 'released' : 'reclaimed'}.`}
      </Toast>,
    );
    logError({
      error: new Error(message),
      errorType: 'ReleaseClaimModalError',
      context: isRelease
        ? 'useRentalManagerUserClaimableListingsWebApiHandler_releaseCustomerListings'
        : 'useRentalManagerUserClaimableListingsWebApiHandler_claimCustomerListings',
    });
  };

  return (
    <Modal
      open={true}
      onOpenChange={(open) => !open && onClose()}
      size={{ base: 'fullScreen', md: 'md' }}
      body={
        <>
          <Heading level={2} textStyle="heading-sm">
            Release your claim and deactivate this listing
          </Heading>
          <TextButton.Root
            onClick={() => window.open(listingRoute, '_blank', 'noopener,noreferrer')}
            css={{ marginTop: '600', marginBottom: '800' }}
          >
            <TextButton.Label css={{ fontSize: 'h4', lineHeight: 'h4' }}>
              {listingDisplayName && listingDisplayName !== address.street && `${listingDisplayName}, `}
              {formattedCompleteAddress}
            </TextButton.Label>
          </TextButton.Root>
          <FieldSet legend={<Legend>Reason for releasing claim</Legend>}>
            {RELEASE_CLAIM_REASONS.map((option) => (
              <LabeledControl
                key={`${RADIO_CONTROL_NAME}-${option.name}`}
                label={<Label>{option.label}</Label>}
                control={
                  <Radio
                    name={RADIO_CONTROL_NAME}
                    onChange={() => setReason(option.name)}
                    checked={reason === option.name}
                  />
                }
              />
            ))}
          </FieldSet>
        </>
      }
      footer={
        <Button
          tone="brand"
          emphasis="filled"
          onClick={handleReleaseClaimClick}
          loading={isLoading}
          data-eventid={releaseClaimDataEventId}
        >
          Release claim
        </Button>
      }
    />
  );
};

export default ReleaseClaimModal;
